import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectsAPI } from '../services/api';

function ProgramArticles() {
  const { programId } = useParams();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getProgramTitle = (id) => {
    const titles = {
      'international-mobility': 'International Mobility Programs',
      'global-citizenship': 'Promotion of Global Citizenship',
      'cooperation-development': 'International Cooperation & Development',
      'youth-empowerment': 'Youth Empowerment & Capacity Building'
    };
    return titles[id] || 'Program Projects';
  };

  useEffect(() => {
    fetchProjects();
  }, [programId]);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, statusFilter, projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll();
      if (response.data && response.data.success) {
        // Filter projects by program category mapping
        const allProjects = response.data.data;
        const mappedProjects = allProjects.filter(project => {
          const category = project.category;
          if (programId === 'international-mobility') {
            return category === 'mobility' || category === 'environment';
          }
          if (programId === 'global-citizenship') {
            return category === 'human_rights';
          }
          if (programId === 'cooperation-development') {
            return category === 'solidarity';
          }
          if (programId === 'youth-empowerment') {
            return category === 'education';
          }
          return false;
        });
        setProjects(mappedProjects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...projects];

    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    setFilteredProjects(filtered);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      mobility: 'International Mobility',
      solidarity: 'Solidarity & Development',
      human_rights: 'Human Rights',
      education: 'Education',
      environment: 'Environment'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <main>
        <div className="slider-area2">
          <div className="slider-height2 d-flex align-items-center">
            <div className="container">
              <div className="row">
                <div className="col-xl-12">
                  <div className="hero-cap hero-cap2 pt-20 text-center">
                    <h2>Loading Program Projects...</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Hero Start */}
      <div className="slider-area2">
        <div className="slider-height2 d-flex align-items-center">
          <div className="container">
            <div className="row">
              <div className="col-xl-12">
                <div className="hero-cap hero-cap2 pt-20 text-center">
                  <h2>{getProgramTitle(programId)}</h2>
                  <p style={{color: 'rgba(255,255,255,0.8)', marginTop: '10px'}}>Explore our initiatives, volunteering activities, and programs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="service-area efis-list-filter-area">
        <div className="container">
          <div className="premium-search-container">
            <div className="row align-items-center">
              <div className="col-lg-6 col-md-12 mb-3 mb-lg-0">
                <input 
                  type="text" 
                  className="premium-search-input" 
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="col-lg-6 col-md-12">
                <select 
                  className="premium-select-filter" 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="our-cases-area efis-list-grid-area">
        <div className="container">
          <div className="row">
            {filteredProjects.length === 0 ? (
              <div className="col-12 text-center" style={{padding: '60px 0'}}>
                <span style={{fontSize: '50px'}}>📂</span>
                <h3 style={{marginTop: '20px', color: '#64748b'}}>No projects found in this category.</h3>
                <Link to="/programs" className="btn" style={{marginTop: '20px'}}>Back to Programs</Link>
              </div>
            ) : (
              filteredProjects.map((project) => (
                <div key={project.id} className="col-lg-4 col-md-6 col-sm-12 mb-4">
                  <div className="single-cases" style={{boxShadow: '0 10px 30px rgba(0,0,0,0.03)', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                    <div className="cases-img" style={{height: '220px', overflow: 'hidden', position: 'relative'}}>
                      <img 
                        src={project.image_url || '/assets/img/gallery/case1.png'} 
                        alt={project.title} 
                        style={{width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s'}}
                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.06)'}
                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                      />
                    </div>
                    <div className="cases-caption p-4" style={{flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                      <div>
                        <span style={{fontSize: '12px', color: 'var(--accent-red)', fontWeight: 'bold', display: 'block', marginBottom: '8px', textTransform: 'uppercase'}}>
                          {getCategoryLabel(project.category)} • {project.location}
                        </span>
                        <h3 style={{fontSize: '18px', fontWeight: '700', lineHeight: '1.4', marginBottom: '15px'}}>
                          <Link to={`/projects/${project.id}`} style={{color: '#1e293b'}}>{project.title}</Link>
                        </h3>
                        <p style={{fontSize: '14px', color: '#64748b', lineHeight: '1.6', height: '70px', overflow: 'hidden', marginBottom: '20px'}}>
                          {project.description}
                        </p>
                      </div>
                      <div>
                        <Link to={`/projects/${project.id}`} className="btn w-100" style={{textAlign: 'center', padding: '10px 20px', fontSize: '14px'}}>Learn More & Apply →</Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default ProgramArticles;
